import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * TableStateManagerService
 * 
 * Táblázat állapot kezelésért felelős service.
 * Kezeli a hover állapotokat, menü állapotokat és kiválasztott cellák állapotát.
 */
@Injectable()
export class TableStateManagerService {

  // Hover state management
  private _hoveredDay = new BehaviorSubject<number | null>(null);
  public readonly hoveredDay$ = this._hoveredDay.asObservable();

  // Menu state management
  private _isMenuOpen = new BehaviorSubject<boolean>(false);
  public readonly isMenuOpen$ = this._isMenuOpen.asObservable();

  private _selectedDay = new BehaviorSubject<number>(-1);
  public readonly selectedDay$ = this._selectedDay.asObservable();

  // Menu trigger reference
  private _currentMenuTrigger: any = null;

  constructor() {}

  /**
   * Jelenlegi hover nap getter
   */
  get currentHoveredDay(): number | null {
    return this._hoveredDay.value;
  }

  /**
   * Jelenlegi kiválasztott nap getter
   */
  get currentSelectedDay(): number {
    return this._selectedDay.value;
  }

  /**
   * Menü nyitva van-e
   */
  get isMenuOpen(): boolean {
    return this._isMenuOpen.value;
  }

  /**
   * Jelenlegi menü trigger getter
   */
  get currentMenuTrigger(): any {
    return this._currentMenuTrigger;
  }

  /**
   * Napra való hover kezelés
   */
  onDayHover(dayIndex: number): void {
    if (!this.isMenuOpen) {
      this._hoveredDay.next(dayIndex);
    }
  }

  /**
   * Napról való leave kezelés  
   */
  onDayLeave(): void {
    if (!this.isMenuOpen) {
      this._hoveredDay.next(null);
    }
  }

  /**
   * Napra való kattintás - jegyzet menü megnyitás
   */
  setSelectedDay(dayIndex: number, trigger: any): void {
    this._selectedDay.next(dayIndex);
    this._currentMenuTrigger = trigger;
    this._isMenuOpen.next(true);

    // Feliratkozás a menü bezárás eseményre (backdrop kattintás, Escape, stb.)
    const subscription = trigger.menuClosed.subscribe(() => {
      this._isMenuOpen.next(false);
      this._hoveredDay.next(null);
      subscription.unsubscribe();
    });

    trigger.openMenu();
  }

  /**
   * Menü akció befejezés
   */
  onMenuAction(): void {
    if (this._currentMenuTrigger) {
      this._currentMenuTrigger.closeMenu();
    }
    this._isMenuOpen.next(false);
    this._hoveredDay.next(null);
  }

  /**
   * Jegyzet ikon megjelenítésének feltétele
   */
  shouldShowNoteIcon(
    dayIndex: number, 
    hasNote: boolean, 
    isSummaryTable: boolean, 
    isWeekend: boolean
  ): boolean {
    return this.currentHoveredDay === dayIndex && 
           !hasNote && 
           !isSummaryTable &&
           !isWeekend;
  }

  /**
   * Jegyzet chip megjelenítésének feltétele
   */
  shouldShowNoteChip(dayIndex: number, hasNote: boolean, isSummaryTable: boolean): boolean {
    return hasNote && !isSummaryTable;
  }

  /**
   * Jegyzet lemondása
   */
  onNoteCancelled(): void {
    this.onMenuAction();
  }

  /**
   * Állapot visszaállítása (reset)
   */
  reset(): void {
    this._hoveredDay.next(null);
    this._isMenuOpen.next(false);
    this._selectedDay.next(-1);
    this._currentMenuTrigger = null;
  }
}